import { Play, Square } from 'lucide-react';
import { FC } from 'react';

import { Button, IconProps, Pressable } from '../../../../shared/ui';
import { MoveCircle } from '../../../icons/move-circle';
import { MoveCross } from '../../../icons/move-cross';
import { MoveEightHorizontal } from '../../../icons/move-eight-horizontal';
import { MoveEightVertical } from '../../../icons/move-eight-vertical';
import { MoveLeft } from '../../../icons/move-left';
import { MoveTop } from '../../../icons/move-top';
import { MoveTopLeft } from '../../../icons/move-top-left';
import { MoveTopRight } from '../../../icons/move-top-right';

import { Trajectory } from '../../../../hooks/useDpdgState';

interface IDpdgControlPanelProps {
  isMoving: boolean;
  onToggleMoving: () => void;
  trajectory: Trajectory;
  changeTrajectory?: (trajectory: Trajectory) => void;
  className?: string;
  loadingProgress?: number;
  /** Мобильный 1:1: две строки на всю ширину (старт/стоп + траектории без горизонтального скролла) */
  compact?: boolean;
}

const trajectoryOptions: Array<{ value: Trajectory; label: string; Icon: FC<IconProps> }> = [
  { value: 'horizontal', label: 'горизонталь', Icon: MoveLeft },
  { value: 'vertical', label: 'вертикаль', Icon: MoveTop },
  { value: 'diagonal-br-tl', label: 'диагональ снизу справа вверх влево', Icon: MoveTopLeft },
  { value: 'diagonal-bl-tr', label: 'диагональ снизу слева вверх вправо', Icon: MoveTopRight },
  { value: 'circle', label: 'круг', Icon: MoveCircle },
  { value: 'eight-vertical', label: 'вертикальная восьмёрка', Icon: MoveEightVertical },
  { value: 'eight-horizontal', label: 'горизонтальная восьмёрка', Icon: MoveEightHorizontal },
  { value: 'cross', label: 'крест', Icon: MoveCross },
];

export const DpdgControlPanel: FC<IDpdgControlPanelProps> = ({
  isMoving,
  onToggleMoving,
  trajectory,
  changeTrajectory,
  className,
  loadingProgress,
  compact = false,
}) => {
  const canChangeTrajectory = typeof changeTrajectory === 'function';
  const shouldShowProgress = typeof loadingProgress === 'number' && loadingProgress >= 0 && loadingProgress < 1;
  const progressWidth = shouldShowProgress ? Math.max(0, Math.min(loadingProgress, 1)) * 100 : 0;

  return (
    <div
      className={
        compact
          ? `relative flex w-full flex-col items-stretch gap-2 bg-black/60 px-3 py-2 backdrop-blur-[2px] ${className ?? ''}`
          : `relative flex w-full flex-nowrap items-center justify-center gap-3 overflow-x-auto bg-black/60 px-4 py-3 backdrop-blur-[2px] ${className ?? ''}`
      }
    >
      {shouldShowProgress && (
        <div className="absolute inset-x-0 top-0 h-[3px] overflow-hidden rounded-t-control bg-white/20">
          <div
            className="h-full bg-accent transition-[width] duration-200 ease-out"
            style={{ width: `${progressWidth}%` }}
          />
        </div>
      )}
      <Button
        size="lg"
        variant="ghostInverse"
        fullWidth={compact}
        className={compact ? undefined : 'min-w-44'}
        startIcon={
          isMoving ? (
            <Square size={16} fill="currentColor" aria-hidden />
          ) : (
            <Play size={16} fill="currentColor" aria-hidden />
          )
        }
        onClick={onToggleMoving}
      >
        {isMoving ? 'Стоп' : 'Старт'}
      </Button>

      <div className={compact ? 'flex w-full items-center justify-between gap-1' : 'flex items-center gap-2'}>
        {trajectoryOptions.map(({ value, label, Icon }) => {
          const isActive = trajectory === value;

          return (
            <Pressable
              key={value}
              aria-pressed={isActive}
              onClick={() => canChangeTrajectory && changeTrajectory?.(value)}
              disabled={!canChangeTrajectory}
              className={`flex items-center justify-center rounded-full border border-white/20 bg-white/10 transition-colors ${
                compact ? 'h-10 w-10 shrink-0' : 'h-12 w-12'
              } ${
                isActive ? 'border-white/60 bg-white/25 shadow-[0_0_12px_rgba(255,255,255,0.25)]' : 'hover:bg-white/20'
              } ${!canChangeTrajectory ? 'opacity-50 hover:bg-white/10' : ''}`}
              aria-label={`Изменить траекторию: ${label}`}
            >
              <Icon />
            </Pressable>
          );
        })}
      </div>
    </div>
  );
};
